import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, Drawer, message, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { saveDocumnetVersionData, setEditorSummary, setReadOnly, updateIsSaving } from '../../../app/EditorSlice';
import { workwiseServices } from '../../../api';
import VersionModalContent from './versionModalContent';
import './versions.css';
import { safeParseEditorState } from '../../../utils/helper';
import MyHeader from '../../Header/Header.module.css';

const VersionsModal = ({ leftRef, portalRef }) => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [showVersionButtons, setShowVersionButtons] = useState(false);

    const documentVersionData = useSelector((state) => state.editor.documentVersionData);
    const lexicalEditor = useSelector((state) => state.editor.lexicalEditor);
    const isReadOnly = useSelector((state) => state.editor.isReadOnly);

    const toggleDrawer = (e) => {
        try {
            e.preventDefault();
            setOpen((prevState) => !prevState);
        } catch (error) {
            console.error('Error toggling the drawer:', error);
        }
    };

    const onClose = (e) => {
        try {
            e.preventDefault();
            setOpen(false);
        } catch (error) {
            console.error('Error closing the drawer:', error);
        }
    };

    const restoreVersion = () => {
        Modal.confirm({
            title: 'Are you sure you want to restore this version?',
            content: 'This will overwrite your current document state.',
            okText: 'Yes, restore',
            cancelText: 'No, keep current version',
            style: {
                zIndex: 9999,
            },
            onOk: () => {
                try {
                    dispatch(setReadOnly(false));
                    setShowVersionButtons(false);
                    dispatch(updateIsSaving({ isSaving: 'Saving...', lastSavedDateTime: '' }));

                    const updateVersionData = { ...documentVersionData, isVersionChange: false };
                    workwiseServices.saveDocumentlastUpdate(id, dispatch, updateVersionData);
                    dispatch(saveDocumnetVersionData(updateVersionData));
                } catch (error) {
                    message.error('Error restoring the version.');
                    console.error('Error restoring the version:', error);
                }
            },
            onCancel() {
                goBackToOriginalVersion();
            },
        });
    };

    const goBackToOriginalVersion = () => {
        try {
            if (lexicalEditor && documentVersionData?.originalData?.editorData) {
                const data = documentVersionData.originalData.editorData;
                const summary = documentVersionData?.originalData?.summary || '';
                const initialEditorState = safeParseEditorState(lexicalEditor, data);
                lexicalEditor.setEditorState(initialEditorState);
                dispatch(setEditorSummary(summary));

                const updateVersionData = { ...documentVersionData, isVersionChange: false };
                dispatch(saveDocumnetVersionData(updateVersionData));
            } else {
                throw new Error('Editor instance or original version data is missing.');
            }
        } catch (error) {
            message.error('Something went wrong while going back to the original version.');
            console.error('Error going back to the original version:', error);
        } finally {
            dispatch(setReadOnly(false));
            setShowVersionButtons(false);
        }
    };

    return (
        <>
            {showVersionButtons && (
                <>
                    {(portalRef && portalRef.current) ? (
                        createPortal(
                            <div className="version-btn-container" style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                <Button
                                    id="version-back-btn"
                                    className="version-button"
                                    onClick={goBackToOriginalVersion}
                                >
                                    Go Back
                                </Button>
                                <Button
                                    id="version-restore-button"
                                    className="version-button"
                                    onClick={restoreVersion}
                                >
                                    Restore this version
                                </Button>
                            </div>,
                            portalRef.current
                        )
                    ) : (
                        leftRef && leftRef.current ? (
                            createPortal(
                                <div className="version-btn-container" style={{ display: 'flex', gap: '5px', alignItems: 'center', marginTop: '-6px' }}>
                                    <Button
                                        id="version-back-btn"
                                        className="version-button"
                                        onClick={goBackToOriginalVersion}
                                    >
                                        Go Back
                                    </Button>
                                    <Button
                                        id="version-restore-button"
                                        className="version-button"
                                        onClick={restoreVersion}
                                    >
                                        Restore this version
                                    </Button>
                                </div>,
                                leftRef.current
                            )
                        ) : (
                            <div className="version-btn-container" style={{ position: 'absolute', left: '100px', top: '12px' }}>
                                <Button
                                    id="version-back-btn"
                                    className="version-button"
                                    onClick={goBackToOriginalVersion}
                                >
                                    Go Back
                                </Button>
                                <Button
                                    id="version-restore-button"
                                    className="version-button"
                                    onClick={restoreVersion}
                                >
                                    Restore this version
                                </Button>
                            </div>
                        )
                    )}
                </>
            )}
            {!isReadOnly && (
                <div
                    title="Document Version"
                    className={MyHeader.iconContainer}
                    onClick={toggleDrawer}
                >
                    <svg className={MyHeader.actionIconImg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-label="Document Versions" role="img"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M12 8v4l3 2" /></svg>
                </div>
            )}
            <Drawer
                title="Version history"
                onClose={onClose}
                visible={open}
                zIndex={9999}
            >
                <VersionModalContent open={open} setOpen={setOpen} setShowVersionButtons={setShowVersionButtons} />
            </Drawer>
        </>
    );
};

export default VersionsModal;
